"use client"

import { useCallback, useEffect, useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { parseDocEnvelopes, parseValueMappingSetData, serializeValueMappingSetData } from "@/lib/global-config"
import { invalidateHubCounts } from "@/lib/hub-counts-refresh"
import { GROUP_NAMES, PROVIDERS, TRANSLATABLE_FIELDS, ValueMappingRow, ValueOption } from "@/types/payroll"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import { Loader2, Plus, Trash2 } from "lucide-react"

interface EditableSet {
  field: string
  docId: string | null
  rows: ValueMappingRow[]
  isSaving: boolean
}

/**
 * Everyday name and explanation per translatable field. The English wording in
 * TRANSLATABLE_FIELDS (types/payroll.ts) stays as the fallback for a field that
 * has no keys here yet, so a new translatable field never renders blank.
 */
const FIELD_KEYS: Record<string, { label: TranslationKey; hint: TranslationKey }> = {
  department: { label: "field.department", hint: "mappings.hint.department" },
  workersCompCode: { label: "field.workersCompCode", hint: "mappings.hint.workersCompCode" },
  workLocation: { label: "field.workLocation", hint: "mappings.hint.workLocation" },
}

/**
 * Value translations: per provider, an optional list per translatable field
 * that turns FBO's internal value into the word/code the provider expects.
 * No list = the FBO value is sent as-is. A list with a value missing from it
 * = a visible error on the Workers tab (never a silent guess) — that rule
 * lives in the backend mapper.
 *
 * The mapped values themselves (FBO value, provider value, note) are tenant
 * configuration data and are never translated.
 */
export function MappingsPage() {
  const { adminApi, payrollApi } = useApi()
  const { toast } = useToast()
  const { t } = useTranslation()

  const fieldLabel = useCallback(
    (field: string) => {
      const keys = FIELD_KEYS[field]
      if (keys) return t(keys.label)
      return TRANSLATABLE_FIELDS.find((f) => f.field === field)?.label ?? field
    },
    [t]
  )

  const fieldHint = useCallback(
    (field: string) => {
      const keys = FIELD_KEYS[field]
      if (keys) return t(keys.hint)
      return TRANSLATABLE_FIELDS.find((f) => f.field === field)?.hint ?? ""
    },
    [t]
  )

  const [provider, setProvider] = useState<string>(PROVIDERS[0])
  const [sets, setSets] = useState<EditableSet[]>([])
  /** Tenant FBO-side values per field (job categories, worker comp codes); empty = lookup unavailable → free text. */
  const [valueOptions, setValueOptions] = useState<Record<string, ValueOption[]>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [raw, ...optionResponses] = await Promise.all([
        adminApi.get<unknown>(API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.valueMappingSet)),
        ...TRANSLATABLE_FIELDS.map((f) =>
          payrollApi
            .get<{ data: ValueOption[] }>(API_ENDPOINTS.PAYROLL.VALUE_OPTIONS(f.field))
            .catch(() => ({ data: [] as ValueOption[] }))
        ),
      ])
      setValueOptions(
        Object.fromEntries(TRANSLATABLE_FIELDS.map((f, i) => [f.field, optionResponses[i].data ?? []]))
      )
      const docs = parseDocEnvelopes(raw).map((d) => ({ id: d.id, data: parseValueMappingSetData(d.data) }))
      setSets(
        TRANSLATABLE_FIELDS.map((f) => {
          const doc = docs.find(
            (d) =>
              d.data.provider.toLowerCase() === provider.toLowerCase() &&
              d.data.field.toLowerCase() === f.field.toLowerCase()
          )
          return {
            field: f.field,
            docId: doc?.id ?? null,
            rows: doc?.data.mappings ?? [],
            isSaving: false,
          }
        })
      )
    } catch (err) {
      // Server error text is shown as-is; only the fallback is translated.
      setError(getApiErrorDetail(err) ?? t("mappings.error.load"))
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  useEffect(() => {
    void load()
  }, [load])

  const updateSet = (field: string, patch: Partial<EditableSet>) =>
    setSets((all) => all.map((s) => (s.field === field ? { ...s, ...patch } : s)))

  const save = async (set: EditableSet) => {
    const cleanRows = set.rows.filter((r) => r.fboValue.trim() || r.providerValue.trim())
    const incomplete = cleanRows.find((r) => !r.fboValue.trim() || !r.providerValue.trim())
    if (incomplete) {
      toast({ variant: "destructive", title: t("mappings.toast.incompleteLine") })
      return
    }
    // Duplicate FBO values would make the translation a silent guess (the
    // mapper takes the first match) — refuse them here, naming the value.
    const seen = new Set<string>()
    for (const row of cleanRows) {
      const key = row.fboValue.trim().toLowerCase()
      if (seen.has(key)) {
        toast({
          variant: "destructive",
          title: t("mappings.toast.duplicateValue", { value: row.fboValue.trim() }),
          description: t("mappings.toast.duplicateValueDescription"),
        })
        return
      }
      seen.add(key)
    }

    updateSet(set.field, { isSaving: true })
    try {
      // Server-truth lookup: re-fetch before deciding create vs update, so a
      // stale docId can never create a duplicate document.
      const path = API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.valueMappingSet)
      const existing = parseDocEnvelopes(await adminApi.get<unknown>(path)).find((d) => {
        const data = parseValueMappingSetData(d.data)
        return (
          data.provider.toLowerCase() === provider.toLowerCase() &&
          data.field.toLowerCase() === set.field.toLowerCase()
        )
      })

      const data = serializeValueMappingSetData({ provider, field: set.field, mappings: cleanRows })
      if (existing?.id) {
        // Spread the server's envelope and replace only Data — Cosmos rejects
        // a replace whose PartitionKey is missing.
        await adminApi.put(path, { ...existing.envelope, Data: data })
      } else {
        await adminApi.post(path, {
          documentType: "Configuration",
          groupName: GROUP_NAMES.valueMappingSet,
          data,
        })
      }

      toast({ title: t("mappings.toast.saved", { field: fieldLabel(set.field) }) })
      // The hub card counts the translation lists in use.
      invalidateHubCounts()
      void load()
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("mappings.toast.saveFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
      updateSet(set.field, { isSaving: false })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">{t("mappings.intro")}</p>
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="w-[170px]" aria-label={t("common.payrollSystem")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PROVIDERS.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="space-y-3 py-8 text-center text-sm">
            <p className="text-destructive">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void load()
                invalidateHubCounts()
              }}
            >
              {t("common.tryAgain")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        sets.map((set) => (
          <Card key={set.field}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-base">{fieldLabel(set.field)}</CardTitle>
                <CardDescription>{fieldHint(set.field)}</CardDescription>
              </div>
              <Button size="sm" onClick={() => void save(set)} disabled={set.isSaving}>
                {set.isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {t("common.save")}
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {set.rows.length > 0 && (
                <>
                  <div className="grid grid-cols-[1fr_1fr_1fr_36px] gap-2 text-xs font-medium text-muted-foreground">
                    <span>{t("mappings.header.fboValue")}</span>
                    <span>{t("mappings.header.providerValue", { provider })}</span>
                    <span>{t("mappings.header.note")}</span>
                    <span />
                  </div>
                  {set.rows.map((row, index) => (
                    <div key={index} className="grid grid-cols-[1fr_1fr_1fr_36px] items-center gap-2">
                      {(valueOptions[set.field] ?? []).length > 0 ? (
                        // The tenant's actual values (job categories / worker
                        // comp codes); the stored value is the id the mapper
                        // emits. A saved value no longer in the list still
                        // renders so it can be seen and replaced.
                        <Select
                          value={row.fboValue}
                          onValueChange={(v) =>
                            updateSet(set.field, {
                              rows: set.rows.map((r, i) => (i === index ? { ...r, fboValue: v } : r)),
                            })
                          }
                        >
                          <SelectTrigger aria-label={t("mappings.aria.fboValue")}>
                            <SelectValue placeholder={t("mappings.placeholder.chooseValue")} />
                          </SelectTrigger>
                          <SelectContent>
                            {valueOptions[set.field].map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label} ({o.value})
                              </SelectItem>
                            ))}
                            {row.fboValue &&
                              !valueOptions[set.field].some((o) => o.value === row.fboValue) && (
                                <SelectItem value={row.fboValue}>
                                  {t("common.notInCurrentListValue", { value: row.fboValue })}
                                </SelectItem>
                              )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          aria-label={t("mappings.aria.fboValue")}
                          placeholder={t("mappings.placeholder.fboValue")}
                          value={row.fboValue}
                          onChange={(e) =>
                            updateSet(set.field, {
                              rows: set.rows.map((r, i) => (i === index ? { ...r, fboValue: e.target.value } : r)),
                            })
                          }
                        />
                      )}
                      <Input
                        aria-label={t("mappings.aria.providerValue")}
                        placeholder={t("mappings.placeholder.providerValue")}
                        value={row.providerValue}
                        onChange={(e) =>
                          updateSet(set.field, {
                            rows: set.rows.map((r, i) => (i === index ? { ...r, providerValue: e.target.value } : r)),
                          })
                        }
                      />
                      <Input
                        aria-label={t("mappings.aria.note")}
                        placeholder={t("mappings.placeholder.note")}
                        value={row.description ?? ""}
                        onChange={(e) =>
                          updateSet(set.field, {
                            rows: set.rows.map((r, i) => (i === index ? { ...r, description: e.target.value } : r)),
                          })
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("mappings.aria.removeLine")}
                        onClick={() => updateSet(set.field, { rows: set.rows.filter((_, i) => i !== index) })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </>
              )}
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    updateSet(set.field, { rows: [...set.rows, { fboValue: "", providerValue: "", description: "" }] })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> {t("mappings.addLine")}
                </Button>
                {set.rows.length > 0 && (
                  <p className="text-xs text-muted-foreground">{t("mappings.notListedNote")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  )
}
